import { useState } from 'react'
import { isHTTPError } from 'ky'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import { AlertCircle, Trash2 } from 'lucide-react'
import { deleteClient } from '@frontend/lib/api'
import type { ClientListItem } from '@common/types'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@frontend/components/ui/dialog'
import { Button } from '@frontend/components/ui/button'
import { Spinner } from '@frontend/components/ui/spinner'

const CONFIRMATION_TEXT = 'confirm irreversible delete'

interface DeleteClientModalProps {
  client: ClientListItem | null
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function DeleteClientModal({ client, open, onOpenChange }: DeleteClientModalProps) {
  const queryClient = useQueryClient()
  const [error, setError] = useState<string | null>(null)
  const [confirmInput, setConfirmInput] = useState('')

  const isConfirmed = confirmInput === CONFIRMATION_TEXT

  const deleteMutation = useMutation({
    mutationFn: () => deleteClient(client!.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clients-detailed'] })
      onOpenChange(false)
      toast.success(`Client "${client!.name}" has been permanently deleted`)
    },
    onError: (err) => {
      if (isHTTPError(err)) {
        const data = err.data as { error?: string }
        setError(data.error || 'Failed to delete client')
      } else {
        setError('Failed to delete client')
      }
    },
  })

  const handleDelete = () => {
    setError(null)
    deleteMutation.mutate()
  }

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) {
      setConfirmInput('')
      setError(null)
      deleteMutation.reset()
    }
    onOpenChange(nextOpen)
  }

  const isSubmitting = deleteMutation.isPending

  if (!client) return null

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-destructive">
            <Trash2 className="h-5 w-5" />
            Delete Client
          </DialogTitle>
          <DialogDescription>
            Are you sure you want to permanently delete <strong>{client.name}</strong>?
          </DialogDescription>
        </DialogHeader>

        <div className="py-4 space-y-4">
          <div className="flex items-start gap-3 p-3 bg-destructive/10 text-destructive rounded-md text-sm">
            <AlertCircle className="h-5 w-5 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-medium">This action is permanent and cannot be undone</p>
              <p className="mt-1">The following data will be permanently deleted:</p>
              <ul className="mt-1 list-disc list-inside space-y-0.5">
                <li>All Drata sync data (pulls, frameworks, controls, requirements, policies, connections)</li>
                <li>All vendor compliance documents generated for this client</li>
                <li>Trust center requests</li>
                <li>Client workspaces</li>
                <li>The client's API key and configuration</li>
              </ul>
            </div>
          </div>

          <div>
            <label htmlFor="confirm-delete" className="block text-sm font-medium mb-1">
              Type <span className="font-mono text-destructive">{CONFIRMATION_TEXT}</span> to confirm
            </label>
            <input
              id="confirm-delete"
              type="text"
              value={confirmInput}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setConfirmInput(e.target.value)}
              placeholder={CONFIRMATION_TEXT}
              disabled={isSubmitting}
              autoComplete="off"
              className="w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>
        </div>

        {/* Error display */}
        {error && (
          <div className="flex items-center gap-2 p-3 bg-destructive/10 text-destructive rounded-md text-sm">
            <AlertCircle className="h-4 w-4 flex-shrink-0" />
            {error}
          </div>
        )}

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => handleOpenChange(false)} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button type="button" variant="destructive" onClick={handleDelete} disabled={!isConfirmed || isSubmitting}>
            {isSubmitting ? (
              <>
                <Spinner size="sm" className="mr-2" />
                Deleting...
              </>
            ) : (
              'Delete Client'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
